import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CreditCard, ArrowRight, QrCode, Gift, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

/*
 * Merchant home. Answers, in this order:
 *   1. Is LoyalPass bringing my customers back? (last 30 days)
 *   2. Who is about to get a reward?
 *   3. What just happened?
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
};

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

const activityLabel = (a) => {
  if (a.type === 'stamp') return '+1 tampon';
  if (a.type === 'redeem') {
    return a.card_type === 'stamps' ? `−${plural(a.points, 'tampon', 'tampons')}` : `−${a.points} pts`;
  }
  return `+${a.points} pts`;
};

/* Last 7 days, oldest first, with empty days kept (a quiet Monday is information too). */
const lastSevenDays = (daily) => {
  const byDay = Object.fromEntries((daily || []).map((d) => [d._id, d.count]));
  const names = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (6 - i));
    const key = d.toISOString().slice(0, 10);
    return { key, label: i === 6 ? 'Auj.' : names[d.getUTCDay()], count: byDay[key] || 0 };
  });
};

const Panel = ({ title, children, className = '', testId }) => (
  <section className={`bg-white rounded-[20px] border border-[#E3E6EA] p-5 ${className}`} data-testid={testId}>
    {title && <h2 className="text-[17px] font-bold text-[#15171A] mb-3">{title}</h2>}
    {children}
  </section>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/analytics/dashboard/simple`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#4A515C]" /></div>;
  if (!data) {
    return (
      <p className="text-center text-[#4A515C] py-20">
        Le tableau de bord n’a pas pu se charger. Rechargez la page.
      </p>
    );
  }

  const firstName = (user?.name || '').split(' ')[0];
  const s = data.summary || { visits: 0, returning_customers: 0, new_customers: 0, rewards: 0, period_days: 30 };
  const near = data.near_reward || [];
  const week = lastSevenDays(data.daily_scans);
  const weekMax = Math.max(1, ...week.map((d) => d.count));
  const weekTotal = week.reduce((a, d) => a + d.count, 0);

  return (
    <div className="space-y-5 max-w-5xl" data-testid="merchant-dashboard">
      <header>
        <h1 className="text-[26px] sm:text-[30px] font-bold text-[#15171A] leading-tight">
          {firstName ? `Bonjour ${firstName}` : 'Tableau de bord'}
        </h1>
        {user?.business_name && <p className="text-[15px] text-[#4A515C] mt-0.5">{user.business_name}</p>}
      </header>

      {data.total_cards === 0 ? (
        <section className="bg-[#15171A] text-white rounded-[22px] p-6 sm:p-8" data-testid="empty-state-cta">
          <CreditCard className="w-9 h-9 mb-4 opacity-90" strokeWidth={1.75} />
          <h2 className="text-[24px] font-bold leading-tight mb-2">Créez votre carte de fidélité</h2>
          <p className="text-[16px] text-white/75 mb-6 max-w-md">
            Couleurs, logo, récompense : 5 minutes, et vos clients peuvent l’ajouter à leur téléphone.
          </p>
          <Link
            to="/dashboard/cards/new"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-white text-[#15171A] text-[16px] font-semibold"
            data-testid="create-first-card-btn"
          >
            Créer ma carte <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      ) : (
        /* 1. The value the merchant pays for */
        <section className="bg-[#15171A] text-white rounded-[22px] p-6 sm:p-7" data-testid="dashboard-kpis">
          <p className="text-[14px] text-white/65">Sur les {s.period_days} derniers jours</p>
          {s.visits === 0 ? (
            <>
              <p className="text-[24px] font-bold leading-tight mt-2">Pas encore de passage</p>
              <p className="text-[16px] text-white/75 mt-2 max-w-md">
                Affichez votre QR boutique en caisse : vos clients s’inscrivent en le scannant.
              </p>
            </>
          ) : (
            <>
              <p className="mt-2 flex items-baseline gap-3 flex-wrap">
                <span className="text-[56px] sm:text-[64px] font-bold leading-none tabular-nums" data-testid="returning-count">
                  {s.returning_customers}
                </span>
                <span className="text-[19px] font-semibold leading-tight">
                  {s.returning_customers > 1 ? 'clients sont revenus' : 'client est revenu'}
                </span>
              </p>
              <dl className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-white/15">
                <div>
                  <dt className="text-[13px] text-white/65">Passages</dt>
                  <dd className="text-[24px] font-bold tabular-nums">{s.visits}</dd>
                </div>
                <div>
                  <dt className="text-[13px] text-white/65">Nouveaux</dt>
                  <dd className="text-[24px] font-bold tabular-nums">{s.new_customers}</dd>
                </div>
                <div>
                  <dt className="text-[13px] text-white/65">Récompenses</dt>
                  <dd className="text-[24px] font-bold tabular-nums">{s.rewards}</dd>
                </div>
              </dl>
            </>
          )}
          <Link
            to="/dashboard/store-qr"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-[15px] font-semibold"
            data-testid="dashboard-store-qr"
          >
            <QrCode className="w-5 h-5" /> Mon QR boutique
          </Link>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* 2. Who is about to get a reward */}
        <Panel title="Bientôt une récompense" testId="near-reward">
          {near.length === 0 ? (
            <p className="text-[15px] text-[#4A515C]">
              Aucun client à 1 ou 2 tampons du cadeau pour l’instant.
            </p>
          ) : (
            <ul className="-mx-2">
              {near.map((n) => (
                <li key={`${n.customer_id}-${n.card_name}`}>
                  <Link
                    to={`/dashboard/customers/${n.customer_id}`}
                    className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-[#F3F4F6]"
                  >
                    <span className="w-9 h-9 rounded-full bg-[#FFF4D6] text-[#7A5200] flex items-center justify-center shrink-0">
                      <Gift className="w-4 h-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[16px] font-semibold text-[#15171A] truncate">{n.name}</span>
                      <span className="block text-[14px] text-[#4A515C] truncate">
                        Plus que {plural(n.remaining, n.unit, `${n.unit}s`)} · {n.card_name}
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#9AA1AB] shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* 3. What just happened */}
        <Panel title="Activité récente">
          {data.activity.length === 0 ? (
            <p className="text-[15px] text-[#4A515C]">Aucun passage pour le moment.</p>
          ) : (
            <ul className="divide-y divide-[#EEF0F2]" data-testid="activity-feed">
              {data.activity.slice(0, 6).map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-[#15171A] truncate">{a.customer_name}</span>
                    <span className={`block text-[14px] ${a.type === 'redeem' ? 'text-[#4A515C]' : 'text-[#1F8A4C]'}`}>{activityLabel(a)}</span>
                  </span>
                  <span className="text-[13px] text-[#5D6470] shrink-0">{timeAgo(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {data.total_cards > 0 && (
        <Panel title="Passages des 7 derniers jours" testId="weekly-chart">
          <p className="text-[14px] text-[#4A515C] -mt-2 mb-4">{plural(weekTotal, 'passage', 'passages')} cette semaine</p>
          <div className="flex items-end gap-2 h-32" role="img" aria-label={`Passages par jour : ${week.map((d) => `${d.label} ${d.count}`).join(', ')}`}>
            {week.map((d) => (
              <div key={d.key} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                <span className="text-[12px] font-semibold text-[#15171A] tabular-nums">{d.count || ''}</span>
                <div
                  className={`w-full max-w-[44px] rounded-md ${d.count ? 'bg-[#15171A]' : 'bg-[#E3E6EA]'}`}
                  style={{ height: `${d.count ? Math.max(8, (d.count / weekMax) * 100) : 4}%` }}
                />
                <span className="text-[12px] text-[#5D6470]">{d.label}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
};

export default DashboardPage;
